import React, { useState } from 'react';
import { EmptyState } from './shared';

const GRADIENT = 'linear-gradient(90deg, #004AAD 0%, #0082FB 100%)';
const GRADIENT_TEXT = {
  background: GRADIENT,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const OutreachSequence = ({ outreachSequence }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [copiedIdx, setCopiedIdx] = useState(null);

  // Process the data to handle both array and object formats
  const processSequence = () => {
    // If it's already an array, clean and return it
    if (Array.isArray(outreachSequence)) {
      return outreachSequence.map(step => {
        const processedStep = { ...step };
        
        // Clean subject if it exists in the step
        if (processedStep.subject) {
          // Remove "Subject:" prefix if present
          processedStep.subject = processedStep.subject.replace(/^Subject:\s*/i, '').trim();
        }
        
        // Check if body contains subject line and extract it if subject is empty
        if (!processedStep.subject && processedStep.body) {
          const subjectMatch = processedStep.body.match(/^Subject:\s*([^\n]+)/i);
          if (subjectMatch) {
            processedStep.subject = subjectMatch[1].trim();
            // Remove the subject line from body
            processedStep.body = processedStep.body.replace(/^Subject:\s*[^\n]+\n?/i, '').trim();
          }
        }
        
        // Clean the body - remove any "Subject:" or "Opening:" or "Hook:" prefixes
        if (processedStep.body) {
          processedStep.body = processedStep.body
            .replace(/^Subject:\s*/i, '') // Remove subject prefix if still there
            .replace(/^Opening:\s*/i, '') // Remove opening prefix for calls
            .replace(/^Hook:\s*/i, '') // Remove hook prefix for calls
            .trim();
        }
        
        return processedStep;
      });
    }
    
    // If it's an object with named fields, convert to array of steps
    if (outreachSequence && typeof outreachSequence === 'object') {
      const steps = [];
      
      // Check for email data
      if (outreachSequence.EMAIL_BODY) {
        let subject = outreachSequence.EMAIL_SUBJECT || '';
        let body = outreachSequence.EMAIL_BODY;
        
        // Clean subject
        subject = subject.replace(/^Subject:\s*/i, '').trim();
        
        // Check if body contains subject line
        const subjectMatch = body.match(/^Subject:\s*([^\n]+)/i);
        if (subjectMatch && !subject) {
          subject = subjectMatch[1].trim();
          body = body.replace(/^Subject:\s*[^\n]+\n?/i, '');
        }
        
        steps.push({
          channel: 'email',
          body: body,
          subject: subject,
        });
      }
      
      // Check for WhatsApp data
      if (outreachSequence.WHATSAPP_MSG) {
        steps.push({
          channel: 'whatsapp',
          body: outreachSequence.WHATSAPP_MSG,
          subject: '',
        });
      }
      
      // Check for call data
      if (outreachSequence.CALL_OPENING || outreachSequence.CALL_HOOK) {
        const callBody = [
          outreachSequence.CALL_OPENING ? `Opening: ${outreachSequence.CALL_OPENING}` : '',
          outreachSequence.CALL_HOOK ? `Hook: ${outreachSequence.CALL_HOOK}` : ''
        ].filter(Boolean).join('\n\n');
        
        steps.push({
          channel: 'call',
          body: callBody,
          subject: '',
        });
      }
      
      return steps;
    }
    
    return [];
  };

  const processedSequence = processSequence();
  
  const emailSteps = processedSequence.filter(s => (s.channel || '').toLowerCase().includes('email'));
  const waSteps = processedSequence.filter(s => (s.channel || '').toLowerCase().includes('whatsapp'));
  const callSteps = processedSequence.filter(s => (s.channel || '').toLowerCase().includes('call'));

  const tabs = [
    emailSteps.length > 0 && { label: 'Email', icon: '✉️', color: 'blue', steps: emailSteps },
    waSteps.length > 0 && { label: 'WhatsApp', icon: '💬', color: '#16a34a', steps: waSteps },
    callSteps.length > 0 && { label: 'Cold Call', icon: '📞', color: '#B5325B', steps: callSteps },
  ].filter(Boolean);

  const allTabs = tabs.length > 0 ? tabs : processedSequence.length > 0
    ? [{ label: 'Sequence', icon: '📌', color: 'blue', steps: processedSequence }]
    : [];

  const safeTab = Math.min(activeTab, allTabs.length - 1);
  const currentTab = allTabs[safeTab];

  const handleCopy = (text, idx) => {
    // Create a temporary textarea for reliable copying
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, 99999);
    
    try {
      document.execCommand('copy');
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch (err) {
      // Fallback to clipboard API
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).catch(() => {});
        setCopiedIdx(idx);
        setTimeout(() => setCopiedIdx(null), 2000);
      }
    }
    
    document.body.removeChild(textarea);
  };

  if (!processedSequence.length) return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: '#0f172a', margin: '0 0 20px', fontFamily: "'Sansation', sans-serif" }}>Outreach Sequence</h2>
      <div style={{ background: '#ffffff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '40px 28px' }}>
        <EmptyState icon="Send" title="No outreach sequences" desc="Email, WhatsApp and call scripts will populate from your HTML report." />
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', color: '#0f172a', margin: 0, fontFamily: "'Sansation', sans-serif" }}>Outreach Sequence</h2>

      {/* Tab pills */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {allTabs.map((tab, idx) => {
          const isActive = idx === safeTab;
          const isBlue = tab.color === 'blue';
          return (
            <button key={idx} onClick={() => setActiveTab(idx)}
              style={{
                fontFamily: "'Sansation', sans-serif",
                fontSize: 14, fontWeight: 600,
                padding: '10px 22px', borderRadius: 12,
                border: `1.5px solid ${isActive ? 'transparent' : '#e2e8f0'}`,
                background: isActive && isBlue ? GRADIENT : isActive ? tab.color : '#ffffff',
                color: isActive ? '#ffffff' : '#64748b',
                cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 8,
                transition: 'all 0.15s',
                boxShadow: isActive && isBlue ? '0 2px 8px rgba(0,74,173,0.22)' : isActive ? `0 2px 8px ${tab.color}33` : 'none',
              }}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>{tab.icon}</span>{tab.label}
            </button>
          );
        })}
      </div>

      {/* Steps */}
      {currentTab && currentTab.steps.map((step, si) => {
        const ch = (step.channel || '').toLowerCase();
        const isEmail = ch.includes('email');
        const isWa = ch.includes('whatsapp');
        const isCall = ch.includes('call');
        let rawBody = step.body || '';
        let subjectLine = step.subject || '';
        
        // Additional cleaning for the specific data format
        if (isEmail) {
          // Check if body starts with the subject line (without "Subject:" prefix)
          if (subjectLine && rawBody.startsWith(subjectLine)) {
            rawBody = rawBody.substring(subjectLine.length).trim();
          }
          
          // Remove any "Subject:" that might be in the body
          rawBody = rawBody.replace(/^Subject:\s*/i, '').trim();
          
          // Add proper spacing after greeting if needed
          rawBody = rawBody.replace(/(Hi\[|Hello\[)/g, '$1'); // Fix any missing spaces
        }
        
        if (isCall) {
          // Format call body to have proper "Opening:" and "Hook:" labels
          if (!rawBody.includes('Opening:') && !rawBody.includes('Hook:')) {
            // If no labels, try to split intelligently
            const sentences = rawBody.split('. ');
            if (sentences.length > 1) {
              rawBody = `Opening: ${sentences[0]}.${sentences.length > 1 ? `\n\nHook: ${sentences.slice(1).join('. ')}` : ''}`;
            }
          }
        }
        
        const dotColor = isWa ? '#16a34a' : isCall ? '#B5325B' : null;

        // Additional cleaning for email body
        if (isEmail) {
          // Fix missing spaces after periods
          rawBody = rawBody.replace(/\.([A-Z])/g, '. $1');
          
          // Fix missing spaces after commas
          rawBody = rawBody.replace(/,([A-Za-z])/g, ', $1');
          
          // Add line break and space after greeting (Hi [Name],I'm -> Hi [Name],\nI'm)
          rawBody = rawBody.replace(/(Hi\[[^\]]+\],)([A-Za-z])/g, '$1\n$2');
          
          // Add space before bullet points
          rawBody = rawBody.replace(/([a-zA-Z])•/g, '$1\n•');
          
          // Add double line break before closing and after closing
          rawBody = rawBody.replace(/(Best,|Thanks,|Regards,)/g, '\n\n$1\n');
          
          // Add line break before founder
          rawBody = rawBody.replace(/(Neeraj Parekh)(Founder)/g, '$1\n$2');
          
          // Add line break before contact info
          rawBody = rawBody.replace(/(\+91)/g, '\n$1');
          rawBody = rawBody.replace(/(www\.)/g, '\n$1');
          
          // Ensure "Neeraj Parekh" and "Founder" are on separate lines
          rawBody = rawBody.replace(/(Neeraj Parekh)(\s*)(Founder)/g, '$1\n$3');
          
          // Clean up multiple line breaks
          rawBody = rawBody.replace(/\n{3,}/g, '\n\n');
        }

        // Format email body with proper structure
        const formatEmailContent = () => {
          if (!isEmail) return null;
          
          // First, intelligently add line breaks to the raw body
          const addLineBreaks = (text) => {
            let formatted = text;
            
            // Add line break and space after greeting (Hi [Name],)
            formatted = formatted.replace(/(Hi\[[^\]]+\],)/g, '$1\n');
            formatted = formatted.replace(/(Hello\[[^\]]+\],)/g, '$1\n');
            formatted = formatted.replace(/(Dear\[[^\]]+\],)/g, '$1\n');
            
            // Add space after greeting if missing (Hi [Name],I'm -> Hi [Name],\nI'm)
            formatted = formatted.replace(/(Hi\[[^\]]+\],)([A-Za-z])/g, '$1\n$2');
            
            // Add line break after sentences that end with periods followed by capital letters
            formatted = formatted.replace(/\.([A-Z])/g, '.\n$1');
            
            // Add line break before bullet points
            formatted = formatted.replace(/•/g, '\n•');
            
            // Add line break and spacing before "Best," "Thanks," etc.
            formatted = formatted.replace(/(Best,)/g, '\n\n$1\n');
            formatted = formatted.replace(/(Thanks,)/g, '\n\n$1\n');
            formatted = formatted.replace(/(Regards,)/g, '\n\n$1\n');
            formatted = formatted.replace(/(Sincerely,)/g, '\n\n$1\n');
            formatted = formatted.replace(/(Cheers,)/g, '\n\n$1\n');
            
            // Add line break before founder/title
            formatted = formatted.replace(/(Neeraj Parekh)(Founder)/g, '$1\n$2');
            formatted = formatted.replace(/(Founder,)/g, '\n$1');
            
            // Add line break before contact info
            formatted = formatted.replace(/(\+91)/g, '\n$1');
            formatted = formatted.replace(/(www\.)/g, '\n$1');
            
            // Clean up multiple line breaks
            formatted = formatted.replace(/\n{3,}/g, '\n\n');
            
            return formatted;
          };
          
          const processedBody = addLineBreaks(rawBody);
          const lines = processedBody.split('\n');
          const elements = [];
          let isInBulletList = false;
          
          lines.forEach((line, index) => {
            const trimmedLine = line.trim();
            
            // Skip empty lines but add spacing
            if (!trimmedLine) {
              elements.push(<div key={`space-${index}`} style={{ height: 8 }} />);
              return;
            }
            
            // Check for bullet points
            if (trimmedLine.startsWith('•')) {
              elements.push(
                <div key={index} style={{ 
                  display: 'flex', 
                  gap: 8, 
                  marginBottom: 8,
                  marginLeft: 16,
                  alignItems: 'flex-start'
                }}>
                  <span style={{ color: '#004AAD', fontSize: 16, lineHeight: 1.4 }}>•</span>
                  <span style={{ fontSize: 13.5, lineHeight: 1.6 }}>{trimmedLine.substring(1).trim()}</span>
                </div>
              );
              isInBulletList = true;
              return;
            }
            
            // Check for signature/contact lines
            if (trimmedLine.includes('@') || trimmedLine.includes('www.') || trimmedLine.match(/\+?\d[\d\s-]{7,}/)) {
              elements.push(
                <div key={index} style={{ 
                  color: '#64748b',
                  fontSize: 13.5,
                  marginBottom: 2,
                  marginTop: elements.length > 0 && lines[index-1]?.trim() ? 4 : 0
                }}>
                  {trimmedLine}
                </div>
              );
              return;
            }
            
            // Check for greeting (Hi [Name],)
            if (/^Hi\[[^\]]+\],/i.test(trimmedLine) || /^Hello\[[^\]]+\],/i.test(trimmedLine) || /^Dear\[[^\]]+\],/i.test(trimmedLine)) {
              elements.push(
                <div key={index} style={{ 
                  marginBottom: 16,
                  fontWeight: 500,
                  fontSize: 13.5,
                }}>
                  {trimmedLine}
                </div>
              );
              return;
            }
            
            // Check for closing (Best, Thanks, etc.)
            if (/^(Best|Regards|Thanks|Sincerely|Cheers),?$/i.test(trimmedLine)) {
              elements.push(
                <div key={index} style={{ 
                  marginTop: 24,
                  marginBottom: 4,
                  fontWeight: 500,
                  fontSize: 13.5,
                }}>
                  {trimmedLine}
                </div>
              );
              return;
            }
            
            // Check for name/founder line
            if (trimmedLine.includes('Neeraj Parekh') || trimmedLine.includes('Founder')) {
              elements.push(
                <div key={index} style={{ 
                  marginBottom: 2,
                  fontSize: 13.5,
                  fontWeight: trimmedLine.includes('Founder') ? 400 : 500,
                  color: trimmedLine.includes('Founder') ? '#4b5563' : '#1f2937'
                }}>
                  {trimmedLine}
                </div>
              );
              return;
            }
            
            // Regular paragraph
            elements.push(
              <div key={index} style={{ 
                margin: isInBulletList ? '0 0 8px' : '0 0 12px',
                fontSize: 13.5,
                lineHeight: 1.7,
                color: '#374151'
              }}>
                {trimmedLine}
              </div>
            );
            isInBulletList = false;
          });
          
          return elements;
        };

        

        // Build the full copy text with proper formatting
        const getCopyText = () => {
          if (isEmail) {
            // Format email for copying with proper line breaks
            let emailText = '';
            if (subjectLine) {
              emailText += `Subject: ${subjectLine}\n\n`;
            }
            
            // Add the body with proper line breaks
            // First, clean up the body to have proper line breaks for copying
            let formattedBody = rawBody
              // Add line break after greeting if missing
              .replace(/(Hi\[[^\]]+\],)([A-Za-z])/g, '$1\n$2')
              // Add line break after sentences
              .replace(/\.([A-Z])/g, '.\n$1')
              // Add line break before bullet points
              .replace(/•/g, '\n•')
              // Add line break before closing
              .replace(/(Best,|Thanks,|Regards,)/g, '\n\n$1\n')
              // Add line break before founder
              .replace(/(Neeraj Parekh)(Founder)/g, '$1\n$2')
              // Add line break before contact info
              .replace(/(\+91)/g, '\n$1')
              .replace(/(www\.)/g, '\n$1')
              // Clean up multiple line breaks
              .replace(/\n{3,}/g, '\n\n');
            
            emailText += formattedBody;
            return emailText;
          } else {
            // For WhatsApp and Call, just return the body
            return rawBody;
          }
        };

        const copyText = getCopyText();

        return (
          <div key={si} style={{ background: '#ffffff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #f1f5f9', background: '#fafafa' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {dotColor ? (
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: dotColor }} />
                ) : (
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: GRADIENT, display: 'inline-block' }} />
                )}
                {dotColor ? (
                  <span style={{ fontSize: 14, fontWeight: 700, color: dotColor }}>{currentTab.label} {si + 1}</span>
                ) : (
                  <span style={{ fontSize: 14, fontWeight: 700, ...GRADIENT_TEXT }}>{currentTab.label} {si + 1}</span>
                )}
              </div>
              <button
                onClick={() => handleCopy(copyText, `${currentTab.label}-${si}`)}
                style={{
                  fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                  padding: '5px 14px', borderRadius: 8,
                  border: '1px solid #e2e8f0', background: '#ffffff',
                  color: copiedIdx === `${currentTab.label}-${si}` ? '#16a34a' : '#64748b',
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
                  transition: 'all 0.15s',
                }}>
                {copiedIdx === `${currentTab.label}-${si}` ? '✅ Copied!' : '📋 Copy'}
              </button>
            </div>

            {/* Email */}
            {isEmail && (
              <div style={{ padding: '24px 28px', background: '#ffffff' }}>
                {/* Email Header */}
                <div style={{ 
                  background: '#f8fafc', 
                  borderRadius: '8px 8px 0 0', 
                  border: '1px solid #e2e8f0', 
                  borderBottom: 'none',
                  padding: '16px 20px',
                }}>
                  {/* From */}
                  <div style={{ display: 'flex', gap: 16, marginBottom: 8, alignItems: 'baseline' }}>
                    <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500, width: 60 }}>From:</span>
                    <span style={{ fontSize: 13, color: '#0f172a' }}>
                      <span style={{ fontWeight: 600 }}>Neeraj Parekh</span> &lt;neeraj@mydeckaudit.com&gt;
                    </span>
                  </div>
                  
                  {/* To */}
                  <div style={{ display: 'flex', gap: 16, marginBottom: 8, alignItems: 'baseline' }}>
                    <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500, width: 60 }}>To:</span>
                    <span style={{ fontSize: 13, color: '#0f172a' }}>
                      [Investor Name] — Partner, [Fund Name]
                    </span>
                  </div>
                  
                  {/* Subject */}
                  {subjectLine && (
                    <div style={{ display: 'flex', gap: 16, alignItems: 'baseline', paddingTop: 8, borderTop: '1px dashed #e2e8f0' }}>
                      <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500, width: 60 }}>Subject:</span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{subjectLine}</span>
                    </div>
                  )}
                </div>
                
                {/* Email Body */}
                <div style={{ 
                  background: '#ffffff', 
                  border: '1px solid #e2e8f0', 
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  padding: '24px 28px',
                }}>
                  <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {formatEmailContent()}
                  </div>
                </div>
              </div>
            )}

            {/* WhatsApp */}
            {isWa && (
              <div style={{ padding: '24px 28px', background: '#e5ddd5' }}>
                <div style={{ maxWidth: 480, margin: '0 auto' }}>
                  {/* WhatsApp Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 18 }}>💬</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>Neeraj Parekh</div>
                      <div style={{ fontSize: 11, color: '#667781' }}>Online</div>
                    </div>
                  </div>
                  
                  {/* Message Bubble */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ 
                      background: '#dcf8c6', 
                      borderRadius: '12px 12px 4px 12px', 
                      padding: '12px 16px', 
                      fontSize: 13.5, 
                      lineHeight: 1.6, 
                      color: '#111', 
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      maxWidth: '100%',
                      wordBreak: 'break-word',
                    }}>
                      {rawBody.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < rawBody.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                      <div style={{ fontSize: 10, color: '#667781', textAlign: 'right', marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
                        {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        <span>✓✓</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cold Call */}
            {isCall && (
              <div style={{ padding: '24px 28px', background: '#1e1e2f' }}>
                <div style={{ 
                  background: '#2d2d3a', 
                  borderRadius: 12, 
                  padding: 20,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                }}>
                  {/* Call Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #40404f' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#B5325B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      📞
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15, color: '#fff' }}>Cold Call Script</div>
                      <div style={{ fontSize: 12, color: '#aaa' }}>Suggested talking points</div>
                    </div>
                  </div>
                  
                  {/* Script Content */}
                  <div style={{ 
                    fontFamily: "'DM Sans', sans-serif", 
                    fontSize: 14, 
                    color: '#e2e8f0', 
                    lineHeight: 1.8,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {rawBody.split('\n').map((line, i) => {
                      if (!line.trim()) return <div key={i} style={{ height: 10 }} />;
                      
                      if (line.includes(':')) {
                        const [prefix, ...rest] = line.split(':');
                        return (
                          <div key={i} style={{ marginBottom: 8 }}>
                            <span style={{ color: '#B5325B', fontWeight: 600 }}>{prefix}:</span>
                            <span style={{ color: '#fff' }}>{rest.join(':')}</span>
                          </div>
                        );
                      }
                      
                      return <div key={i} style={{ marginBottom: 8 }}>{line}</div>;
                    })}
                  </div>
                  
                  {/* Call Tip */}
                  <div style={{ marginTop: 16, padding: 12, background: '#23232e', borderRadius: 6, fontSize: 12, color: '#aaa' }}>
                  </div>
                </div>
              </div>
            )}

          </div>
        );
      })}
    </div>
  );
};

export default OutreachSequence;